import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import echo from "@utils/echo";
import { useTranslation } from "react-i18next";
import { getAccessToken } from "context/OAuth/authService";
 
const SocketContext = createContext();
 
export const SocketProvider = ({ user, children }) => {
  const [exerciseProgress, setExerciseProgress] = useState(null);
  const [dietProgress, setDietProgress] = useState(null);
  const [singleMealQueue, setSingleMealQueue] = useState([]);
  const [sleepCoachProgress, setSleepCoachProgress] = useState(null);
  const [mindFullnessProgress, setMindFullnessProgress] = useState(null);
  const [motivationCoachProgress, setMotivationCoachProgress] = useState(null);
  const { t } = useTranslation();
 
  useEffect(() => {
    const token = getAccessToken();
    if (!user?.id || !token) return;
 
    echo.updateToken(token);
    const socket = echo.connector.socket;
 
    if (socket && !socket.connected) socket.connect();
 
    socket.on("connect", () => {
      console.log("✅ Echo connected:", socket.id);
    });
 
    socket.on("connect_error", (err) => {
      console.error("❌ Echo connection failed:", err);
    });
 
    // Diet Progress Channel
    const dietChannel = echo.private(`dietplanProgress.${user.id}`);
    dietChannel.listen(".progress.update", (e) => {
      console.log("🍎 Diet progress update:", e);
      setDietProgress(e);
      if (e.status === "failed") {
        toast.error(e?.error ?? "Your diet plan generation failed");
      }
    });
 
    const recreateMealChanel = echo.private(`singleMeal.${user.id}`);
    recreateMealChanel.listen(".meal.generated", (e) => {
      console.log("🍎 Single meal result:", e);
      setSingleMealQueue((prev) => [...prev, e]);
      if (e.status === "failed") {
        toast.error(e?.error ?? "Your meal generation failed");
      }
    });
 
    // Exercise Progress Channel
    const exerciseChannel = echo.private(`exercisePlanProgress.${user.id}`);
    exerciseChannel.listen(".exercise.progress.update", (e) => {
      console.log("💪 Exercise progress update:", e);
      setExerciseProgress(e);
      if (e.status === "failed") {
        toast.error(
          "Error in creating your new Exercise Plan. Please try again."
        );
      }
    });
 
    // Sleep Coach Progress Channel
    const sleepCoachChannel = echo.private(`sleepCoachProgress.${user.id}`);
    sleepCoachChannel.listen(".progress.update", (e) => {
      console.log("😴 Sleep coach progress update:", e);
      setSleepCoachProgress(e);
      if (e.status === "failed") {
        toast.error(t("Failed to create plan! , please try again."));
      }
    });
 
    // Mindfulness Progress Channel
    const mindFullnessChannel = echo.private(`mindFullnessProgress.${user.id}`);
    mindFullnessChannel.listen(".progress.update", (e) => {
      console.log("🧠 Mindfulness progress update:", e);
      setMindFullnessProgress(e);
      if (e.status === "failed") {
        toast.error(t("Failed to create plan! , please try again."));
      }
    });
 
    // Motivation Coach Progress Channel - NEW
    const motivationCoachChannel = echo.private(`motivationCoachProgress.${user.id}`);
    motivationCoachChannel.listen(".progress.update", (e) => {
      console.log("🔥 Motivation coach progress update:", e);
      setMotivationCoachProgress(e);
      if (e.status === "failed") {
        toast.error(t("Failed to create motivation plan! Please try again."));
      }
    });
 
    return () => {
      // Stop listening to all channels
      dietChannel.stopListening(".progress.update");
      exerciseChannel.stopListening(".exercise.progress.update");
      sleepCoachChannel.stopListening(".progress.update");
      mindFullnessChannel.stopListening(".progress.update");
      motivationCoachChannel.stopListening(".progress.update"); // NEW
      recreateMealChanel.stopListening(".meal.generated");
 
      // Leave all channels
      echo.leave(`dietplanProgress.${user.id}`);
      echo.leave(`exercisePlanProgress.${user.id}`);
      echo.leave(`sleepCoachProgress.${user.id}`);
      echo.leave(`mindFullnessProgress.${user.id}`);
      echo.leave(`motivationCoachProgress.${user.id}`); // NEW
      echo.leave(`singleMeal.${user.id}`);
 
      socket.off("connect");
      socket.off("connect_error");
    };
  }, [user?.id, t]);
 
  // ✅ Clear after component uses the value
  const clearExerciseProgress = () => setExerciseProgress(null);
  const clearDietProgress = () => setDietProgress(null);
  const clearSleepCoachProgress = () => setSleepCoachProgress(null);
  const clearMindFullnessProgress = () => setMindFullnessProgress(null);
  const clearMotivationCoachProgress = () => setMotivationCoachProgress(null); // NEW
  const consumeSingleMealResult = () =>
    setSingleMealQueue((prev) => prev.slice(1));

  return (
    <SocketContext.Provider
      value={{
        exerciseProgress,
        dietProgress,
        sleepCoachProgress,
        mindFullnessProgress,
        motivationCoachProgress,
        singleMealResult: singleMealQueue[0] ?? null,
        singleMealQueue,
        clearExerciseProgress,
        clearDietProgress,
        clearSleepCoachProgress,
        clearMindFullnessProgress,
        clearMotivationCoachProgress,
        clearSingleMealResult: consumeSingleMealResult,
        consumeSingleMealResult,
      }}
    >
      {children}
</SocketContext.Provider>
  );
};
 
export const useSocket = () => useContext(SocketContext);